import { useState, useEffect, useCallback } from 'react';
import * as Sentry from "@sentry/react";

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

interface PWACapabilities {
  isInstallable: boolean;
  isInstalled: boolean;
  isStandalone: boolean;
  canInstall: boolean;
  platform: 'ios' | 'android' | 'desktop' | 'unknown';
}

export function usePWA() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [platform, setPlatform] = useState<PWACapabilities['platform']>('unknown');

  // Detect platform
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    const isDesktop = !isIOS && !isAndroid;

    if (isIOS) setPlatform('ios');
    else if (isAndroid) setPlatform('android');
    else if (isDesktop) setPlatform('desktop');
  }, []);

  // Check if app is running in standalone mode
  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                     (window.navigator as any).standalone ||
                     document.referrer.includes('android-app://');
    setIsStandalone(standalone);
    setIsInstalled(standalone);
  }, []);

  // Listen for install prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const installApp = useCallback(async () => {
    if (!installPrompt) return { success: false, error: 'No install prompt available' };

    try {
      await installPrompt.prompt();
      const choiceResult = await installPrompt.userChoice;
      
      if (choiceResult.outcome === 'accepted') {
        setInstallPrompt(null);
        return { success: true, outcome: choiceResult.outcome };
      } else {
        return { success: false, outcome: choiceResult.outcome };
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'pwa_install', component: 'usePWA' },
        extra: { message: 'Install failed' }
      });
      return { success: false, error: error instanceof Error ? error.message : 'Install failed' };
    }
  }, [installPrompt]);

  const shareApp = useCallback(async (url?: string, title?: string) => {
    const shareUrl = url || window.location.href;
    const shareTitle = title || 'mySuccession - Business Succession Planning';
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          url: shareUrl,
        });
        return { success: true };
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          Sentry.captureException(error, {
            tags: { context: 'pwa_share', component: 'usePWA' },
            extra: { message: 'Share failed', url: shareUrl }
          });
        }
        return { success: false, error: error instanceof Error ? error.message : 'Share failed' };
      }
    } else {
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        return { success: true, fallback: 'clipboard' };
      } catch (error) {
        return { success: false, error: 'Share not supported and clipboard access denied' };
      }
    }
  }, []);

  const capabilities: PWACapabilities = {
    isInstallable: !!installPrompt,
    isInstalled,
    isStandalone,
    canInstall: !!installPrompt && !isInstalled,
    platform
  };

  // iOS specific install instructions
  const getIOSInstallInstructions = useCallback(() => {
    const isIOSSafari = /safari/.test(navigator.userAgent.toLowerCase()) && 
                       !/chrome|crios|fxios/.test(navigator.userAgent.toLowerCase());
    
    if (platform === 'ios' && !isStandalone) {
      return {
        canInstall: isIOSSafari,
        instructions: isIOSSafari 
          ? 'Tap the Share button, then "Add to Home Screen"'
          : 'Open this page in Safari to install the app'
      };
    }
    return null;
  }, [platform, isStandalone]);

  return {
    ...capabilities,
    installApp,
    shareApp,
    installPrompt: !!installPrompt,
    iosInstructions: getIOSInstallInstructions()
  };
}